import {
  canEditGuestProfile,
  canManageGuestLoyalty,
  canManageGuestMarketing,
  canMergeGuestProfile,
  canViewGuestProfile,
} from "./permissions.ts";
import {
  validateCreateGuestProfileRequest,
  validateEnrollGuestLoyaltyRequest,
  validateMergeGuestProfilesRequest,
  validateSyncGuestStayHistoryRequest,
  validateUpdateGuestContactDetailsRequest,
  validateUpdateGuestMarketingRequest,
  validateUpsertGuestPreferenceRequest,
} from "./contracts/schemas.ts";
import type {
  CreateGuestProfileRequestDto,
  EnrollGuestLoyaltyRequestDto,
  MergeGuestProfilesRequestDto,
  SyncGuestStayHistoryRequestDto,
  UpdateGuestContactDetailsRequestDto,
  UpdateGuestMarketingRequestDto,
  UpsertGuestPreferenceRequestDto,
} from "./contracts/dtos.ts";
import type {
  GuestAddressContact,
  GuestContactDetails,
  GuestEmailContact,
  GuestLoyaltyProfile,
  GuestPreferenceCategory,
  GuestMarketingProfile,
  GuestPhoneContact,
  GuestPreference,
  GuestProfile,
  GuestProfileContext,
  GuestProfileContextGuest,
  GuestStayHistoryEntry,
} from "./types.ts";

type IdFactory = (prefix: string) => string;

function hasErrors(fieldErrors: Record<string, string>) {
  return Object.values(fieldErrors).some(Boolean);
}

function normalizeEmail(value: string) {
  return value.trim().toLowerCase();
}

function normalizePhone(value: string) {
  return value.replace(/\s+/g, "").trim();
}

function normalizeText(value: string | undefined) {
  return value?.trim() || "";
}

function nightsBetween(arrival: string, departure: string) {
  const start = new Date(`${arrival}T00:00:00`);
  const end = new Date(`${departure}T00:00:00`);
  const diff = Math.max(0, end.getTime() - start.getTime());
  return Math.max(1, Math.round(diff / 86400000));
}

function loyaltyTierRank(tier: GuestLoyaltyProfile["tier"]) {
  if (tier === "platinum") return 4;
  if (tier === "gold") return 3;
  if (tier === "silver") return 2;
  return 1;
}

function toLegacyPreferenceBuckets(preferences: GuestPreference[]) {
  return preferences
    .filter((entry) => entry.isActive && entry.scope === "global")
    .reduce<{ room: string[]; service: string[]; comms: string[] }>(
      (acc, entry) => {
        if (entry.category === "room_type" || entry.category === "room_location" || entry.category === "pillow" || entry.category === "bed") {
          acc.room.push(entry.label);
        } else if (entry.category === "communication") {
          acc.comms.push(entry.label);
        } else {
          acc.service.push(entry.label);
        }
        return acc;
      },
      { room: [], service: [], comms: [] }
    );
}

function mergeUnique<T>(left: T[], right: T[], keyFn: (value: T) => string) {
  const map = new Map<string, T>();
  for (const entry of [...left, ...right]) {
    map.set(keyFn(entry), entry);
  }
  return [...map.values()];
}

function normalizeContactDetails(
  guest: GuestProfileContextGuest,
  profile: GuestProfile | undefined,
  idFactory: IdFactory
): GuestContactDetails {
  const base: GuestContactDetails = profile?.contactDetails ?? {
    emails: [],
    phones: [],
    addresses: [],
    preferredLanguage: guest.language,
    marketingOptIn: false,
    emailOptIn: false,
    smsOptIn: false,
    doNotContact: false,
  };

  const email = normalizeEmail(guest.email);
  const addressLine = normalizeText(guest.address);
  const phone = normalizePhone(guest.phone ?? "");

  const emails = email
    ? mergeUnique(
        base.emails,
        [{ id: idFactory("guest-email"), email, label: "primary" as const, isPrimary: true, verified: true }],
        (entry) => normalizeEmail(entry.email)
      )
    : base.emails;
  const phones = phone
    ? mergeUnique(
        base.phones,
        [{ id: idFactory("guest-phone"), phone, label: "mobile" as const, isPrimary: true }],
        (entry) => normalizePhone(entry.phone)
      )
    : base.phones;
  const addresses = addressLine
    ? mergeUnique(
        base.addresses,
        [{ id: idFactory("guest-address"), addressLine, label: "primary" as const, isPrimary: true }],
        (entry) => normalizeText(entry.addressLine).toLowerCase()
      )
    : base.addresses;

  return {
    ...base,
    preferredLanguage: base.preferredLanguage ?? guest.language,
    emails: emails.map((entry, index) => ({ ...entry, isPrimary: entry.isPrimary || index === 0 })),
    phones: phones.map((entry, index) => ({ ...entry, isPrimary: entry.isPrimary || index === 0 })),
    addresses: addresses.map((entry, index) => ({ ...entry, isPrimary: entry.isPrimary || index === 0 })),
  };
}

function normalizeProfile(
  guest: GuestProfileContextGuest,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
): GuestProfile {
  const existing = guest.crmProfile;
  const preferences = existing?.preferences ?? [
    ...(guest.preferences?.room ?? []).map((label) => ({
      id: dependencies.idFactory("guest-pref"),
      guestId: guest.id,
      category: (label.toLowerCase().includes("pillow") ? "pillow" : "room_location") as GuestPreferenceCategory,
      label,
      scope: "global" as const,
      source: "derived" as const,
      notedAt: context.now,
      updatedAt: context.now,
      isActive: true,
    })),
    ...(guest.preferences?.service ?? []).map((label) => ({
      id: dependencies.idFactory("guest-pref"),
      guestId: guest.id,
      category: "service" as GuestPreferenceCategory,
      label,
      scope: "global" as const,
      source: "derived" as const,
      notedAt: context.now,
      updatedAt: context.now,
      isActive: true,
    })),
    ...(guest.preferences?.comms ?? []).map((label) => ({
      id: dependencies.idFactory("guest-pref"),
      guestId: guest.id,
      category: "communication" as GuestPreferenceCategory,
      label,
      scope: "global" as const,
      source: "derived" as const,
      notedAt: context.now,
      updatedAt: context.now,
      isActive: true,
    })),
  ];

  return {
    id: existing?.id ?? dependencies.idFactory("guest-profile"),
    guestId: guest.id,
    status: existing?.status ?? "active",
    duplicateOfGuestId: existing?.duplicateOfGuestId,
    mergedGuestIds: existing?.mergedGuestIds ?? [],
    createdAt: existing?.createdAt ?? context.now,
    updatedAt: context.now,
    contactDetails: normalizeContactDetails(guest, existing, dependencies.idFactory),
    preferences,
    loyalty: existing?.loyalty,
    marketing: existing?.marketing ?? {
      segmentIds: guest.segment ? [guest.segment.toLowerCase().replace(/\s+/g, "_")] : ["general"],
      segmentLabels: guest.segment ? [guest.segment] : ["General"],
      tags: guest.vip ? ["vip"] : [],
      source: "derived",
    },
    stayHistory: existing?.stayHistory ?? [],
    notes: existing?.notes ?? [],
  };
}

function getGuestOrFail(guestId: string, context: GuestProfileContext) {
  return context.guests.find((entry) => entry.id === guestId) ?? null;
}

function detectDuplicates(
  guestId: string,
  context: GuestProfileContext
) {
  const guest = getGuestOrFail(guestId, context);
  if (!guest) return [];

  const normalizedGuestEmail = normalizeEmail(guest.email);
  const normalizedDocument = normalizeText(guest.documentNumber).toLowerCase();

  return context.guests.filter((entry) => {
    if (entry.id === guestId) return false;
    if (entry.crmProfile?.status === "merged") return false;
    if (normalizedGuestEmail && normalizeEmail(entry.email) === normalizedGuestEmail) return true;
    if (normalizedDocument && normalizeText(entry.documentNumber).toLowerCase() === normalizedDocument) return true;
    return false;
  });
}

export function buildGuestStayHistory(
  guestId: string,
  context: GuestProfileContext
) {
  const reservations = context.reservations
    .filter((reservation) => reservation.guestId === guestId)
    .map<GuestStayHistoryEntry>((reservation) => {
      const stay = context.stays.find((entry) => entry.reservationId === reservation.id && entry.guestId === guestId);
      return {
        id: stay?.id ?? `history-${reservation.id}`,
        guestId,
        reservationId: reservation.id,
        stayId: stay?.id,
        roomId: stay?.roomId ?? reservation.roomId,
        roomTypeId: reservation.roomTypeId,
        arrival: stay?.arrival ?? reservation.arrival,
        departure: stay?.departure ?? reservation.departure,
        source: reservation.source,
        status: stay?.status ?? (reservation.status === "booked" || reservation.status === "pending_confirmation" || reservation.status === "checkin" ? "reserved" : reservation.status),
        totalNights: nightsBetween(reservation.arrival, reservation.departure),
        totalRevenue: reservation.pricingSummary?.totalAmount,
        preferenceIds: [],
      };
    });

  return reservations.sort((left, right) => `${right.arrival}`.localeCompare(left.arrival));
}

export function syncGuestProfile(
  guest: GuestProfileContextGuest,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
) {
  const profile = normalizeProfile(guest, context, dependencies);
  const stayHistory = buildGuestStayHistory(guest.id, context).map((entry) => ({
    ...entry,
    preferenceIds: profile.preferences
      .filter((preference) => preference.scope === "stay" && (preference.stayId === entry.stayId || preference.reservationId === entry.reservationId))
      .map((preference) => preference.id),
  }));

  const realizedStays = stayHistory.filter((entry) => entry.status === "inhouse" || entry.status === "checkedout");
  const totalNights = realizedStays.reduce((sum, entry) => sum + entry.totalNights, 0);
  const totalSpend = realizedStays.reduce((sum, entry) => sum + (entry.totalRevenue ?? 0), 0);

  return {
    ...profile,
    stayHistory,
    loyalty: profile.loyalty
      ? {
          ...profile.loyalty,
          stayCount: Math.max(profile.loyalty.stayCount, realizedStays.length),
          lifetimeNights: Math.max(profile.loyalty.lifetimeNights, totalNights),
          lifetimeSpend: Math.max(profile.loyalty.lifetimeSpend, totalSpend),
          lastActivityAt: context.now,
        }
      : undefined,
    updatedAt: context.now,
  };
}

function forbidden(message = "You do not have permission to perform this action.") {
  return { ok: false as const, code: "FORBIDDEN" as const, message };
}

export function createGuestProfile(
  request: CreateGuestProfileRequestDto,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateCreateGuestProfileRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, code: "VALIDATION_ERROR" as const, message: "Guest profile request is invalid.", fieldErrors };
  }
  if (!canEditGuestProfile(request.actorRole)) return forbidden();

  const guest = getGuestOrFail(request.guestId, context);
  if (!guest) return { ok: false as const, code: "GUEST_NOT_FOUND" as const, message: "Guest not found." };

  const duplicates = detectDuplicates(request.guestId, context);
  if (duplicates.length > 0) {
    return {
      ok: false as const,
      code: "DUPLICATE_GUEST_PROFILE" as const,
      message: "A matching guest profile already exists.",
      duplicates: duplicates.map((entry) => ({ guestId: entry.id, email: entry.email, documentNumber: entry.documentNumber })),
    };
  }

  return {
    ok: true as const,
    guestId: guest.id,
    profile: syncGuestProfile(
      {
        ...guest,
        crmProfile: guest.crmProfile?.id ? guest.crmProfile : { ...normalizeProfile(guest, context, dependencies), id: request.profileId },
      },
      context,
      dependencies
    ),
  };
}

export function updateGuestContactDetails(
  request: UpdateGuestContactDetailsRequestDto,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateUpdateGuestContactDetailsRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, code: "VALIDATION_ERROR" as const, message: "Contact details are invalid.", fieldErrors };
  }
  if (!canEditGuestProfile(request.actorRole)) return forbidden();

  const guest = getGuestOrFail(request.guestId, context);
  if (!guest) return { ok: false as const, code: "GUEST_NOT_FOUND" as const, message: "Guest not found." };

  const profile = syncGuestProfile(guest, context, dependencies);
  const mergedEmails = mergeUnique<GuestEmailContact>(
    profile.contactDetails.emails,
    (request.emails ?? []).map((entry) => ({
      id: entry.id ?? dependencies.idFactory("guest-email"),
      email: normalizeEmail(entry.email),
      label: entry.label,
      isPrimary: entry.isPrimary,
      verified: entry.verified,
    })),
    (entry) => normalizeEmail(entry.email)
  );
  const mergedPhones = mergeUnique<GuestPhoneContact>(
    profile.contactDetails.phones,
    (request.phones ?? []).map((entry) => ({
      id: entry.id ?? dependencies.idFactory("guest-phone"),
      phone: normalizePhone(entry.phone),
      label: entry.label,
      isPrimary: entry.isPrimary,
    })),
    (entry) => normalizePhone(entry.phone)
  );
  const mergedAddresses = mergeUnique<GuestAddressContact>(
    profile.contactDetails.addresses,
    (request.addresses ?? []).map((entry) => ({
      id: entry.id ?? dependencies.idFactory("guest-address"),
      addressLine: normalizeText(entry.addressLine),
      city: normalizeText(entry.city) || undefined,
      postalCode: normalizeText(entry.postalCode) || undefined,
      country: normalizeText(entry.country) || undefined,
      label: entry.label,
      isPrimary: entry.isPrimary,
    })),
    (entry) => normalizeText(entry.addressLine).toLowerCase()
  );

  const contactDetails: GuestContactDetails = {
    ...profile.contactDetails,
    preferredLanguage: request.preferredLanguage ?? profile.contactDetails.preferredLanguage,
    marketingOptIn: request.marketingOptIn ?? profile.contactDetails.marketingOptIn,
    emailOptIn: request.emailOptIn ?? profile.contactDetails.emailOptIn,
    smsOptIn: request.smsOptIn ?? profile.contactDetails.smsOptIn,
    doNotContact: request.doNotContact ?? profile.contactDetails.doNotContact,
    emails: mergedEmails.map((entry, index) => ({ ...entry, isPrimary: entry.isPrimary || index === 0 })),
    phones: mergedPhones.map((entry, index) => ({ ...entry, isPrimary: entry.isPrimary || index === 0 })),
    addresses: mergedAddresses.map((entry, index) => ({ ...entry, isPrimary: entry.isPrimary || index === 0 })),
  };

  return {
    ok: true as const,
    guestId: guest.id,
    profile: {
      ...profile,
      contactDetails,
      updatedAt: context.now,
    },
  };
}

export function upsertGuestPreference(
  request: UpsertGuestPreferenceRequestDto,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateUpsertGuestPreferenceRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, code: "VALIDATION_ERROR" as const, message: "Preference is invalid.", fieldErrors };
  }
  if (!canEditGuestProfile(request.actorRole)) return forbidden();

  const guest = getGuestOrFail(request.guestId, context);
  if (!guest) return { ok: false as const, code: "GUEST_NOT_FOUND" as const, message: "Guest not found." };

  const profile = syncGuestProfile(guest, context, dependencies);

  if (request.scope === "stay") {
    const stayMatch = request.stayId
      ? context.stays.find((entry) => entry.id === request.stayId && entry.guestId === request.guestId)
      : undefined;
    const reservationMatch = request.reservationId
      ? context.reservations.find((entry) => entry.id === request.reservationId && entry.guestId === request.guestId)
      : undefined;
    if (!stayMatch && !reservationMatch) {
      return {
        ok: false as const,
        code: "INVALID_STAY_REFERENCE" as const,
        message: "Stay-level preferences must point to a stay or reservation owned by this guest.",
      };
    }
  }

  const preference: GuestPreference = {
    id: request.preferenceId,
    guestId: guest.id,
    category: request.category,
    label: request.label.trim(),
    code: request.code?.trim() || undefined,
    value: request.value?.trim() || undefined,
    scope: request.scope,
    stayId: request.stayId,
    reservationId: request.reservationId,
    source: request.source ?? "manual",
    notedAt: context.now,
    updatedAt: context.now,
    isActive: request.isActive ?? true,
  };

  const nextProfile = syncGuestProfile(
    {
      ...guest,
      crmProfile: {
        ...profile,
        preferences: [
          preference,
          ...profile.preferences.filter((entry) => entry.id !== request.preferenceId),
        ],
      },
    },
    context,
    dependencies
  );

  return {
    ok: true as const,
    guestId: guest.id,
    profile: nextProfile,
    legacyPreferences: toLegacyPreferenceBuckets(nextProfile.preferences),
  };
}

export function enrollGuestLoyalty(
  request: EnrollGuestLoyaltyRequestDto,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateEnrollGuestLoyaltyRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, code: "VALIDATION_ERROR" as const, message: "Loyalty request is invalid.", fieldErrors };
  }
  if (!canManageGuestLoyalty(request.actorRole)) return forbidden();

  const guest = getGuestOrFail(request.guestId, context);
  if (!guest) return { ok: false as const, code: "GUEST_NOT_FOUND" as const, message: "Guest not found." };

  const profile = syncGuestProfile(guest, context, dependencies);
  const loyalty: GuestLoyaltyProfile = {
    programId: request.programId.trim(),
    memberId: request.memberId.trim(),
    tier: request.tier,
    status: "active" as const,
    pointsBalance: request.pointsBalance ?? profile.loyalty?.pointsBalance ?? 0,
    lifetimePoints: request.lifetimePoints ?? profile.loyalty?.lifetimePoints ?? 0,
    lifetimeNights: request.lifetimeNights ?? profile.loyalty?.lifetimeNights ?? 0,
    lifetimeSpend: request.lifetimeSpend ?? profile.loyalty?.lifetimeSpend ?? 0,
    stayCount: request.stayCount ?? profile.loyalty?.stayCount ?? 0,
    enrolledAt: profile.loyalty?.enrolledAt ?? context.now,
    lastActivityAt: context.now,
    notes: request.notes?.trim() || profile.loyalty?.notes,
  };

  return {
    ok: true as const,
    guestId: guest.id,
    profile: {
      ...profile,
      loyalty,
      updatedAt: context.now,
    },
  };
}

export function updateGuestMarketing(
  request: UpdateGuestMarketingRequestDto,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateUpdateGuestMarketingRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, code: "VALIDATION_ERROR" as const, message: "Marketing segmentation is invalid.", fieldErrors };
  }
  if (!canManageGuestMarketing(request.actorRole)) return forbidden();

  const guest = getGuestOrFail(request.guestId, context);
  if (!guest) return { ok: false as const, code: "GUEST_NOT_FOUND" as const, message: "Guest not found." };

  const profile = syncGuestProfile(guest, context, dependencies);
  const marketing: GuestMarketingProfile = {
    segmentIds: [...new Set(request.segmentIds.map((entry) => entry.trim()).filter(Boolean))],
    segmentLabels: [...new Set(request.segmentLabels.map((entry) => entry.trim()).filter(Boolean))],
    tags: [...new Set((request.tags ?? []).map((entry) => entry.trim()).filter(Boolean))],
    source: request.source,
    lastCampaignAt: request.lastCampaignAt ?? profile.marketing.lastCampaignAt,
  };

  return {
    ok: true as const,
    guestId: guest.id,
    profile: {
      ...profile,
      contactDetails: {
        ...profile.contactDetails,
        marketingOptIn: request.marketingOptIn ?? profile.contactDetails.marketingOptIn,
        emailOptIn: request.emailOptIn ?? profile.contactDetails.emailOptIn,
        smsOptIn: request.smsOptIn ?? profile.contactDetails.smsOptIn,
        doNotContact: request.doNotContact ?? profile.contactDetails.doNotContact,
      },
      marketing,
      updatedAt: context.now,
    },
  };
}

export function mergeGuestProfiles(
  request: MergeGuestProfilesRequestDto,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateMergeGuestProfilesRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, code: "VALIDATION_ERROR" as const, message: "Merge request is invalid.", fieldErrors };
  }
  if (!canMergeGuestProfile(request.actorRole)) return forbidden();

  const targetGuest = getGuestOrFail(request.targetGuestId, context);
  const sourceGuest = getGuestOrFail(request.sourceGuestId, context);
  if (!targetGuest) return { ok: false as const, code: "TARGET_GUEST_NOT_FOUND" as const, message: "Target guest not found." };
  if (!sourceGuest) return { ok: false as const, code: "SOURCE_GUEST_NOT_FOUND" as const, message: "Source guest not found." };

  const targetProfile = syncGuestProfile(targetGuest, context, dependencies);
  const sourceProfile = syncGuestProfile(sourceGuest, context, dependencies);
  const sourceTier = sourceProfile.loyalty?.tier;
  const targetTier = targetProfile.loyalty?.tier;

  return {
    ok: true as const,
    targetGuestId: request.targetGuestId,
    sourceGuestId: request.sourceGuestId,
    mergedProfile: {
      ...targetProfile,
      mergedGuestIds: [...new Set([...targetProfile.mergedGuestIds, request.sourceGuestId, ...sourceProfile.mergedGuestIds])],
      contactDetails: {
        ...targetProfile.contactDetails,
        marketingOptIn: targetProfile.contactDetails.marketingOptIn || sourceProfile.contactDetails.marketingOptIn,
        emailOptIn: targetProfile.contactDetails.emailOptIn || sourceProfile.contactDetails.emailOptIn,
        smsOptIn: targetProfile.contactDetails.smsOptIn || sourceProfile.contactDetails.smsOptIn,
        doNotContact: targetProfile.contactDetails.doNotContact || sourceProfile.contactDetails.doNotContact,
        emails: mergeUnique(targetProfile.contactDetails.emails, sourceProfile.contactDetails.emails, (entry) => normalizeEmail(entry.email)),
        phones: mergeUnique(targetProfile.contactDetails.phones, sourceProfile.contactDetails.phones, (entry) => normalizePhone(entry.phone)),
        addresses: mergeUnique(targetProfile.contactDetails.addresses, sourceProfile.contactDetails.addresses, (entry) => normalizeText(entry.addressLine).toLowerCase()),
      },
      preferences: mergeUnique(
        targetProfile.preferences,
        sourceProfile.preferences.map((entry) => ({ ...entry, guestId: request.targetGuestId })),
        (entry) => `${entry.scope}:${entry.category}:${entry.label.toLowerCase()}:${entry.stayId ?? entry.reservationId ?? "global"}`
      ),
      marketing: {
        segmentIds: [...new Set([...targetProfile.marketing.segmentIds, ...sourceProfile.marketing.segmentIds])],
        segmentLabels: [...new Set([...targetProfile.marketing.segmentLabels, ...sourceProfile.marketing.segmentLabels])],
        tags: [...new Set([...targetProfile.marketing.tags, ...sourceProfile.marketing.tags])],
        source: targetProfile.marketing.source === sourceProfile.marketing.source ? targetProfile.marketing.source : "hybrid",
        lastCampaignAt: targetProfile.marketing.lastCampaignAt ?? sourceProfile.marketing.lastCampaignAt,
      },
      stayHistory: mergeUnique(
        targetProfile.stayHistory,
        sourceProfile.stayHistory.map((entry) => ({ ...entry, guestId: request.targetGuestId })),
        (entry) => entry.reservationId
      ),
      loyalty: targetProfile.loyalty || sourceProfile.loyalty
        ? {
            programId: targetProfile.loyalty?.programId ?? sourceProfile.loyalty?.programId ?? "loyalty",
            memberId: targetProfile.loyalty?.memberId ?? sourceProfile.loyalty?.memberId ?? dependencies.idFactory("loyalty"),
            tier:
              sourceTier && (!targetTier || loyaltyTierRank(sourceTier) > loyaltyTierRank(targetTier))
                ? sourceTier
                : targetTier ?? "base",
            status: "active" as const,
            pointsBalance: (targetProfile.loyalty?.pointsBalance ?? 0) + (sourceProfile.loyalty?.pointsBalance ?? 0),
            lifetimePoints: (targetProfile.loyalty?.lifetimePoints ?? 0) + (sourceProfile.loyalty?.lifetimePoints ?? 0),
            lifetimeNights: (targetProfile.loyalty?.lifetimeNights ?? 0) + (sourceProfile.loyalty?.lifetimeNights ?? 0),
            lifetimeSpend: (targetProfile.loyalty?.lifetimeSpend ?? 0) + (sourceProfile.loyalty?.lifetimeSpend ?? 0),
            stayCount: (targetProfile.loyalty?.stayCount ?? 0) + (sourceProfile.loyalty?.stayCount ?? 0),
            enrolledAt: targetProfile.loyalty?.enrolledAt ?? sourceProfile.loyalty?.enrolledAt ?? context.now,
            lastActivityAt: context.now,
            notes: [targetProfile.loyalty?.notes, sourceProfile.loyalty?.notes].filter(Boolean).join(" | ") || undefined,
          }
        : undefined,
      updatedAt: context.now,
    },
    sourceProfile: {
      ...sourceProfile,
      status: "merged" as const,
      duplicateOfGuestId: request.targetGuestId,
      updatedAt: context.now,
    },
  };
}

export function syncGuestStayHistory(
  request: SyncGuestStayHistoryRequestDto,
  context: GuestProfileContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateSyncGuestStayHistoryRequest(request);
  if (hasErrors(fieldErrors)) {
    return { ok: false as const, code: "VALIDATION_ERROR" as const, message: "Stay history request is invalid.", fieldErrors };
  }
  if (!canViewGuestProfile(request.actorRole)) return forbidden();

  const guest = getGuestOrFail(request.guestId, context);
  if (!guest) return { ok: false as const, code: "GUEST_NOT_FOUND" as const, message: "Guest not found." };

  return {
    ok: true as const,
    guestId: guest.id,
    profile: syncGuestProfile(guest, context, dependencies),
    stayHistory: buildGuestStayHistory(guest.id, context),
  };
}
